import React, { useEffect, useState } from "react";
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { GraphPanel, StatPanel } from "components/panel";
import { CreditHistory } from "pages/licence-checking/shared/credit-history/CreditHistory";
import { LicenceCheckingService } from "services/LicenceCheckingService";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { GridLayout } from "components/grid/GridLayout";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useQuery } from "hooks/useQuery";
import axios from "axios";

export function LicenceCheckingCreditsStats() {
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    const requiredAction = "LicenceChecking:Manage";
    const creditExpiryLocation = "/licence-checking/credits/expiry";
    const hasAccess = UserHasAccess(requiredAction);

    // Same query-forwarding as the shell's default-tab redirect, so the grey fleet breadcrumb survives.
    const query = useQuery();
    const creditExpiryLink =
        query.get("from") === "greyfleet"
            ? `${creditExpiryLocation}?from=greyfleet`
            : creditExpiryLocation;
    const licenceCheckingCredits = useSelector((state) => state.licenceChecking.licenceCredits);
    const greyFleetCredits = useSelector((state) => state.licenceChecking.greyFleetCredits);

    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [licenceCheckingCreditStatistics, setLicenceCheckingCreditStatistics] = useState({
        thisWeek: 0,
        thisMonth: 0,
        singleUse: 0,
    });
    const [greyFleetCreditStatistics, setGreyFleetCreditStatistics] = useState({
        thisWeek: 0,
        thisMonth: 0,
    });
    const [licenceCheckingOptions, setLicenceCheckingOptions] = useState(null);
    const [greyFleetOptions, setGreyFleetOptions] = useState(null);

    useEffect(() => {
        initalise();
    }, [hasAccess]);

    function initalise() {
        if (!hasAccess) {
            return;
        }

        setLoading(true);

        axios
            .all([
                LicenceCheckingService.getCredits(),
                LicenceCheckingService.getCreditsForecast(),
            ])
            .then(
                axios.spread(function (
                    getCreditsResponse,
                    getCreditsForecastResponse
                ) {
                    dispatch({
                        type: "SET_LICENCE_CREDITS",
                        payload: getCreditsResponse.licenceCredits,
                    });
                    dispatch({
                        type: "SET_GREY_FLEET_CREDITS",
                        payload: getCreditsResponse.greyFleetCredits,
                    });

                    setLicenceCheckingCreditStatistics({
                        thisWeek: getCreditsForecastResponse.licenceCheck.currentUsage.week,
                        thisMonth:
                            getCreditsForecastResponse.licenceCheck.currentUsage.month,
                        singleUse:
                            getCreditsForecastResponse.licenceCheck.currentUsage
                                .manualChecksThisMonth,
                    });

                    setGreyFleetCreditStatistics({
                        thisWeek: getCreditsForecastResponse.greyFleet.currentUsage.week,
                        thisMonth:
                            getCreditsForecastResponse.greyFleet.currentUsage.month,
                    });

                    setLicenceCheckingOptions(mapGraph(getCreditsForecastResponse.licenceCheck.monthlyUsage));
                    setGreyFleetOptions(mapGraph(getCreditsForecastResponse.greyFleet.monthlyUsage));
                })
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Failed to get credit statistics. Please refresh the page."
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function mapGraph(monthlyUsage) {
        let yearMonths = [];
        let usage = [];
        let forecast = [];

        monthlyUsage.forEach((m) => {
            var xAxisId =
                months[m.month - 1] +
                " " +
                (m.year.toString().length >= 4
                    ? m.year.toString().substring(2)
                    : m.year);
            yearMonths = [...yearMonths, xAxisId];
            usage = [...usage, { name: xAxisId, value: m.usage }];
            forecast = [...forecast, { name: xAxisId, value: m.forecast }];
        });

        let graphSeries = [
            {
                name: "Usage",
                type: "bar",
                stack: "total",
                data: usage,
            },
            {
                name: "Forecast",
                type: "bar",
                stack: "total",
                data: forecast,
            },
        ];

        let graphXAxis = {
            data: yearMonths,
        };

        let graphYAxis = {
            type: "value",
            nameLocation: "middle",
            splitNumber: 2,
        };

        return {
            backgroundColor: "#FAFAFA",
            color: ["#008CFF", "#FF7454"],
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    // Use axis to trigger tooltip
                    type: "shadow", // 'shadow' as default; can also be 'line' or 'shadow'
                },
            },
            grid: {
                left: "20px",
                right: "20px",
                bottom: "20px",
                top: "70px",
                containLabel: true,
            },
            xAxis: graphXAxis,
            yAxis: graphYAxis,
            legend: {
                top: "20px",
                selectedMode: false,
            },
            series: graphSeries,
        };
    }

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <>
                    <h2>Licence Credit Statistics</h2>

                    <div className="spacer-bottom">
                        <GridLayout customColumnRatios={[1, 2]} gap={0}>
                            <GridLayout numberOfColumns={2} gap={0}>
                                <StatPanel
                                    title="Licence Checking Credits"
                                    value={licenceCheckingCredits}
                                    loading={loading}
                                />
                                <StatPanel
                                    title="Credits Used This Week"
                                    value={licenceCheckingCreditStatistics.thisWeek}
                                    loading={loading}
                                />
                                <StatPanel
                                    title="Credits Used This Month"
                                    value={licenceCheckingCreditStatistics.thisMonth}
                                    loading={loading}
                                />
                                <StatPanel
                                    title="Single Use Credits Used This Month"
                                    value={licenceCheckingCreditStatistics.singleUse}
                                    loading={loading}
                                />
                            </GridLayout>
                            <GraphPanel
                                title="Credit Usage and Forecast Chart"
                                options={licenceCheckingOptions}
                                loading={loading}
                            />
                        </GridLayout>
                    </div>

                     <h2>Grey Fleet Credit Statistics</h2>

                    <div className="spacer-bottom">
                        <GridLayout customColumnRatios={[1, 2]} gap={0}>
                            <GridLayout numberOfColumns={2} gap={0}>
                                <StatPanel
                                    title="Grey Fleet Credits"
                                    value={greyFleetCredits}
                                    loading={loading}
                                />
                                <StatPanel
                                    title="Credits Used This Week"
                                    value={greyFleetCreditStatistics.thisWeek}
                                    loading={loading}
                                />
                                <StatPanel
                                    title="Credits Used This Month"
                                    value={greyFleetCreditStatistics.thisMonth}
                                    loading={loading}
                                />
                                <div className='stat-panel'></div>
                            </GridLayout>
                            <GraphPanel
                                title="Credit Usage and Forecast Chart"
                                options={greyFleetOptions}
                                loading={loading}
                            />
                        </GridLayout>
                    </div>

                    <p id="credit-expiry-footnote" className="helper-text">
                        See <Link to={creditExpiryLink}>Credit Expiry</Link> for
                        when each batch runs out.
                    </p>

                    <CreditHistory summary={true} />
                </>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
